import { useEffect, useState } from "react";
import {
  getAdminInmateAccess,
  approveInmateAccess,
  rejectInmateAccess,
  revokeInmateAccess,
} from "../api/inmateAccess";
import AdminNav from "../components/AdminNav";
import AsyncButton from "../components/AsyncButton";
import StatusMessage from "../components/StatusMessage";
import { getErrorMessage } from "../utils/apiErrors";

export default function AdminInmateAccess() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [error, setError] = useState("");

  const loadRecords = async () => {
    setLoading(true);

    try {
      const res = await getAdminInmateAccess();
      setRecords(res.data.data || []);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load client access requests."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, []);

  const handleApprove = async (id) => {
    const confirmed = await window.confirm("Approve this attorney's access to the matched client after verifying representation?");
    if (!confirmed || actionId) return;
    await runAction(id, () => approveInmateAccess(id));
  };

  const handleReject = async (id) => {
    const reason = await window.appPrompt({ title: "Reject client access", label: "Reason", message: "Provide a concise operational reason without privileged information.", confirmText: "Reject", variant: "danger" });
    if (!reason || actionId) return;
    await runAction(id, () => rejectInmateAccess(id, reason));
  };

  const handleRevoke = async (id) => {
    const reason = await window.appPrompt({ title: "Revoke client access", label: "Reason", message: "This immediately removes document authorization. Do not include privileged information.", confirmText: "Revoke", variant: "danger" });
    if (!reason || actionId) return;
    await runAction(id, () => revokeInmateAccess(id, reason));
  };

  async function runAction(id, action) {
    setActionId(id);
    setError("");
    try {
      await action();
      await loadRecords();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setActionId(null);
    }
  }

  return (
    <div className="app-shell">
      <div className="topbar d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <div className="brand-mark">CV</div>
          <div>
            <h5 className="mb-0">CounselVault</h5>
            <div className="small opacity-75">Attorney Client Access Review</div>
          </div>
        </div>
       <AdminNav />
      </div>

      <main className="page-wrap">
        <StatusMessage type="warning">{error}</StatusMessage>
        <div className="d-flex justify-content-between align-items-end mb-4">
          <div>
            <h2 className="mb-1">Client / Inmate Access</h2>
            <p className="text-muted mb-0">
              Review attorney requests to submit privileged legal documents for specific inmates.
            </p>
          </div>

          <div className="stat-card px-4 py-3 text-end">
            <div className="text-muted small">Requests</div>
            <h3 className="mb-0">{records.length}</h3>
          </div>
        </div>

        <div className="portal-card">
          <div className="portal-card-header d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">Facility Client Access Requests</h5>
              <div className="text-muted small">
                Attorneys can only upload for inmates approved here.
              </div>
            </div>

            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={loadRecords}
            >
              Refresh
            </button>
          </div>

          <div className="portal-card-body">
            {loading ? (
              <p className="mb-0">Loading client access requests...</p>
            ) : records.length === 0 ? (
              <div className="text-center py-5">
                <h5>No client access requests</h5>
                <p className="text-muted mb-0">
                  Attorney requests for specific inmates will appear here.
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Attorney</th>
                      <th>Requested Inmate</th>
                      <th>Matched Inmate</th>
                      <th>Certification</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {records.map((record) => {
                      const attorney =
                        record.attorney_profile?.user ||
                        record.attorneyProfile?.user;

                      const lawFirm =
                        record.attorney_profile?.law_firm ||
                        record.attorneyProfile?.lawFirm;

                      return (
                        <tr key={record.id}>
                          <td>
                            <strong>{attorney?.name || "Attorney"}</strong>
                            <div className="text-muted small">
                              {attorney?.email || "-"}
                            </div>
                            <div className="text-muted small">
                              {lawFirm?.name || "No law firm listed"}
                            </div>
                          </td>

                          <td>
                            #{record.inmate_number_submitted}
                            <div className="text-muted small">
                              Last name: {record.inmate_last_name_submitted}
                            </div>
                          </td>

                          <td>
                            {record.inmate ? (
                              <>
                                <strong>
                                  {record.inmate.first_name} {record.inmate.last_name}
                                </strong>
                                <div className="text-muted small">
                                  Inmate #{record.inmate.inmate_number}
                                </div>
                              </>
                            ) : (
                              <span className="text-danger small">
                                No inmate match
                              </span>
                            )}
                          </td>

                          <td>
                            {record.representation_acknowledged ? (
                              <span className="status-pill status-approved">
                                Certified
                              </span>
                            ) : (
                              <span className="status-pill status-rejected">
                                Missing
                              </span>
                            )}
                          </td>

                          <td>
                            <span className={`status-pill status-${record.status}`}>
                              {record.status}
                            </span>
                          </td>

                          <td className="text-end">
                            <div className="btn-group">
                              {record.status === "pending" && (
                                <>
                                  <AsyncButton
                                    className="btn btn-sm btn-success"
                                    busy={actionId === record.id}
                                    disabled={actionId !== null}
                                    busyText="Approving…"
                                    onClick={() => handleApprove(record.id)}
                                  >
                                    Approve
                                  </AsyncButton>

                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    disabled={actionId !== null}
                                    onClick={() => handleReject(record.id)}
                                  >
                                    Reject
                                  </button>
                                </>
                              )}

                              {record.status === "approved" && (
                                <AsyncButton
                                  className="btn btn-sm btn-outline-danger"
                                  busy={actionId === record.id}
                                  disabled={actionId !== null}
                                  busyText="Revoking…"
                                  onClick={() => handleRevoke(record.id)}
                                >
                                  Revoke
                                </AsyncButton>
                              )}

                              {["rejected", "revoked"].includes(record.status) && (
                                <AsyncButton
                                  className="btn btn-sm btn-outline-success"
                                  busy={actionId === record.id}
                                  disabled={actionId !== null}
                                  busyText="Approving…"
                                  onClick={() => handleApprove(record.id)}
                                >
                                  Re-approve
                                </AsyncButton>
                              )}

                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
